import { useContext, useState } from 'react';
import { Grid, Box, IconButton, Tooltip, Stack } from '@mui/material';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import {
    DataGrid,
    GridColDef,
    GridToolbar,
    GridToolbarContainer,
    GridRenderCellParams,
    GridValueGetterParams,
} from '@mui/x-data-grid';
import CustomPagination from '../../GlobalComponent/CustomPagination/CustomPagination';
import pendingRequestsStyle from '../../Styles/ComponentStyles/PendingRequests/PendingRequests.module.scss';
import adminDashboardStyles from '../../Styles/ComponentStyles/AdminDashboard/AdminDashboard.module.scss';
import agent from '../../service/api';
import { UserContext, UserContextPayload } from '../../Context/UserDetailContext';
import { useSnackbar } from '../../GlobalComponent/GlobalSnackbar/GlobalSnackbar';
import GlobalPopUp from '../../GlobalComponent/GlobalPopup/GlobalPopup';
import RejectPendingRequestPopupContent, {
    RejectPendingRequestConfirmPayload,
} from './RejectPendingRequestPopupContent';
import { LicensesHoldCell } from './LicensesHoldCell';
import { renderOverflowTextCell } from './OverflowTooltipCell';

type EmailVerificationStatus = 'Verified' | 'Pending';

export interface PendingAgentRow {
    user_id: number;
    name: string;
    email: string;
    requestedDate: string;
    emailVerification: EmailVerificationStatus;
    licensesHold: string[];
    [key: string]: unknown;
}

interface PendingRequestsListProps {
    rows: PendingAgentRow[];
    onRefresh: () => void;
}

const adminDashboardDataGridSx = {
    borderRadius: '5px',
    overflow: 'hidden',
    "& .MuiDataGrid-columnHeaders": {
        backgroundColor: "#F2F6F9",
        color: '#45556C',
        fontSize: '14px',
        fontWeight: 600,
        textTransform: 'uppercase',
        border: 'none',
        minHeight: "54px !important"
    },
    "& .MuiDataGrid-columnHeaderTitleContainer": {
        justifyContent: "start !important"
    },
    "& .MuiDataGrid-columnHeader": {
        padding: '0 16px',
        outline: 'none !important',
        justifyContent: 'flex-start',
    },
    "& .MuiDataGrid-columnHeaderTitle": {
        fontWeight: 600,
        textAlign: 'left',
    },
    "& .MuiDataGrid-main": {
        backgroundColor: "#ffffff",
        borderRadius: "5px 5px 0px 0px",
        borderTop: "1px solid #E2E8F0",
        borderLeft: "1px solid #E2E8F0",
        borderRight: "1px solid #E2E8F0",
        borderBottom: "none",
    },
    "& .MuiDataGrid-row": {
        backgroundColor: "#ffffff",
        color: "#0F172B",
        borderBottom: '1px solid #E2E8F0 !important',
        '&:hover': {
            backgroundColor: '#f3f4f6 !important',
        },
    },
    "& .MuiDataGrid-cell": {
        borderBottom: 'none',
        padding: '0 16px',
        fontSize: '14px !important',
        outline: 'none !important',
    },
    "& .MuiDataGrid-cell > div": {
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
    },
    "& .MuiDataGrid-cell:focus": {
        outline: 'none !important',
    },
    "& .MuiDataGrid-cell:focus-within": {
        outline: 'none !important',
    },
    "& .MuiDataGrid-cell:hover": {
        cursor: "pointer",
    },
    "& .MuiDataGrid-columnSeparator": {
        display: 'none',
    },
    "& .MuiDataGrid-footerContainer": {
        borderTop: "none",
        borderLeft: "1px solid #E2E8F0",
        borderRight: "1px solid #E2E8F0",
        borderBottom: "1px solid #E2E8F0",
        backgroundColor: '#F2F6F9',
        minHeight: '52px',
        padding: '0 16px',
        borderRadius: "0px 0px 5px 5px"
    },
    "& .MuiTablePagination-root": {
        color: '#6b7280',
    },
    "& .MuiTablePagination-actions": {
        color: '#6b7280',
    },
    "& .MuiTablePagination-actions .MuiIconButton-root": {
        color: "#6b7280",
    },
    "& .MuiTablePagination-actions .MuiIconButton-root.Mui-disabled": {
        color: "#d1d5db",
    },
    "& .MuiTablePagination-displayedRows": {
        color: '#6b7280',
        fontSize: '14px',
    },
    "& .MuiDataGrid-toolbarContainer .MuiButtonBase-root": {
        color: "#525252 !important"
    },
    "& .MuiDataGrid-toolbarContainer": {
        margin: "5px !important"
    },
    "& .MuiDataGrid-overlay": {
        backgroundColor: "#ffffff",
    },
    "& .MuiDataGrid-columnHeader--moving": {
        backgroundColor: "transparent",
    },
    "& .MuiDataGrid-virtualScrollerContent": {
        marginTop: "18px"
    }
} as const;

const CustomToolbar = () => (
    <GridToolbarContainer
        sx={{
            margin: '5px 0px 25px',
            float: 'right',
            '& .MuiButtonBase-root': { color: '#ffffff !important' },
        }}
    >
        <GridToolbar />
    </GridToolbarContainer>
);

function NoRowsOverlay() {
    return (
        <Stack sx={{ justifyContent: 'center', height: '100%', alignItems: 'center', color: '#0F172B' }}>
            No Data Found
        </Stack>
    );
}

const PendingRequestsList = ({ rows, onRefresh }: PendingRequestsListProps) => {
    const { setFlagLoader } = useContext(UserContext) as UserContextPayload;
    const { showSnackbar } = useSnackbar();

    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rowPendingReject, setRowPendingReject] = useState<PendingAgentRow | null>(null);
    const [rejectSubmitting, setRejectSubmitting] = useState(false);

    const handleApprove = async (row: PendingAgentRow) => {
        if (!row.user_id || Number.isNaN(row.user_id)) {
            showSnackbar('Invalid user.', 'error');
            return;
        }
        try {
            setFlagLoader(true);
            const response = await agent.approveAgentAPI.post({ user_id: row.user_id });
            if (response?.isAxiosError) {
                showSnackbar('Failed to approve agent.', 'error');
                return;
            }
            if (response?.status) {
                showSnackbar(
                    response?.data?.message || 'User successfully authorized.',
                    'success'
                );
                onRefresh();
            } else {
                showSnackbar(
                    response?.data?.error?.message ||
                    response?.error?.message ||
                    'Failed to approve agent.',
                    'error'
                );
            }
        } catch {
            showSnackbar('Failed to approve agent.', 'error');
        } finally {
            setFlagLoader(false);
        }
    };

    const openRejectDialog = (row: PendingAgentRow) => {
        if (!row.user_id || Number.isNaN(row.user_id)) {
            showSnackbar('Invalid user.', 'error');
            return;
        }
        setRowPendingReject(row);
        setRejectDialogOpen(true);
    };

    const closeRejectDialog = () => {
        if (rejectSubmitting) return;
        setRejectDialogOpen(false);
        setRowPendingReject(null);
    };

    const submitReject = async (payload: RejectPendingRequestConfirmPayload) => {
        const row = rowPendingReject;
        if (!row?.user_id || Number.isNaN(row.user_id)) {
            showSnackbar('Invalid user.', 'error');
            return;
        }
        if (!payload.rejection_note.trim()) {
            showSnackbar('Please enter a reason for rejection.', 'error');
            return;
        }
        try {
            setRejectSubmitting(true);
            setFlagLoader(true);
            const response = await agent.rejectAgentAPI.post({
                user_id: row.user_id,
                rejection_note: payload.rejection_note.trim(),
                notify_agent_on_rejection: payload.notify_agent_on_rejection,
            });
            if (response?.isAxiosError) {
                showSnackbar('Failed to reject agent.', 'error');
                return;
            }
            if (response?.status) {
                showSnackbar(
                    response?.data?.message || 'User successfully denied.',
                    'success'
                );
                setRejectDialogOpen(false);
                setRowPendingReject(null);
                onRefresh();
            } else {
                showSnackbar(
                    response?.data?.error?.message ||
                    response?.error?.message ||
                    'Failed to reject agent.',
                    'error'
                );
            }
        } catch {
            showSnackbar('Failed to reject agent.', 'error');
        } finally {
            setRejectSubmitting(false);
            setFlagLoader(false);
        }
    };

    const handleResendVerification = async (row: PendingAgentRow) => {
        if (!row.user_id || Number.isNaN(row.user_id)) {
            showSnackbar('Invalid user.', 'error');
            return;
        }
        try {
            setFlagLoader(true);
            const response = await agent.resendVerificationEmailAPI.post({ user_id: row.user_id });
            if (response?.isAxiosError) {
                showSnackbar('Failed to resend verification email.', 'error');
                return;
            }
            if (response?.status) {
                const msg =
                    response?.data?.message ||
                    'A confirmation link will be sent to the email.';
                showSnackbar(msg, 'success');
                // No onRefresh() here — resending an email doesn't change
                // any row data or counts, so a full refetch is unnecessary.
            } else {
                showSnackbar(
                    response?.data?.error?.message ||
                    response?.error?.message ||
                    'Failed to resend verification email.',
                    'error'
                );
            }
        } catch {
            showSnackbar('Failed to resend verification email.', 'error');
        } finally {
            setFlagLoader(false);
        }
    };

    const columns: GridColDef[] = [
        {
            field: '__index',
            headerName: '#',
            width: 70,
            sortable: false,
            disableColumnMenu: true,
            valueGetter: (params: GridValueGetterParams) => {
                const ids = params.api.getAllRowIds();
                const i = ids.indexOf(params.id);
                return i === -1 ? '' : i + 1;
            },
        },
        { field: 'name', headerName: 'NAME', flex: 1, minWidth: 140, renderCell: renderOverflowTextCell },
        { field: 'email', headerName: 'EMAIL', flex: 1.5, minWidth: 200, renderCell: renderOverflowTextCell },
        {
            field: 'licensesHold',
            headerName: 'LICENSES HELD',
            flex: 1.2,
            minWidth: 180,
            sortable: false,
            renderCell: (params: GridRenderCellParams<string[], PendingAgentRow>) => (
                <LicensesHoldCell licenses={params.row.licensesHold ?? []} />
            ),
        },
        { field: 'requestedDate', headerName: 'REQUESTED DATE', flex: 1.3, minWidth: 180, renderCell: renderOverflowTextCell },
        {
            field: 'emailVerification',
            headerName: 'EMAIL VERIFICATION',
            flex: 1.2,
            minWidth: 180,
            sortable: false,
            renderCell: (params: GridRenderCellParams<EmailVerificationStatus, PendingAgentRow>) => {
                const isVerified = params.value === 'Verified';
                return (
                    <Box className={pendingRequestsStyle.verificationCell}>
                        <span className={isVerified ? pendingRequestsStyle.verifiedChip : pendingRequestsStyle.pendingChip}>
                            {params.value}
                        </span>
                        {!isVerified && (
                            <Tooltip title="Resend verification email" arrow placement="top">
                                <IconButton
                                    size="small"
                                    onClick={() => handleResendVerification(params.row)}
                                    className={pendingRequestsStyle.resendEmailBtn}
                                >
                                    <MarkEmailUnreadOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                );
            },
        },
        {
            field: 'action',
            headerName: 'ACTION',
            flex: 1.3,
            minWidth: 200,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params: GridRenderCellParams<unknown, PendingAgentRow>) => {
                if (params.row.emailVerification !== 'Verified') return null;
                return (
                    <Box className={pendingRequestsStyle.actionCell}>
                        <button type="button" className={pendingRequestsStyle.approveBtn} onClick={() => handleApprove(params.row)}>
                            Approve
                        </button>
                        <button type="button" className={pendingRequestsStyle.rejectBtn} onClick={() => openRejectDialog(params.row)}>
                            Reject
                        </button>
                    </Box>
                );
            },
        },
    ];

    return (
        <>
            {rejectDialogOpen && rowPendingReject && (
                <GlobalPopUp
                    open={rejectDialogOpen}
                    onClose={closeRejectDialog}
                    title="Reject Request"
                    showCloseIcon
                    flagDivider
                    maxWidth="sm"
                    fullWidth
                >
                    <RejectPendingRequestPopupContent
                        open={rejectDialogOpen}
                        agentDisplayName={rowPendingReject.name}
                        onCancel={closeRejectDialog}
                        onConfirm={submitReject}
                        isSubmitting={rejectSubmitting}
                    />
                </GlobalPopUp>
            )}
            <Grid container direction="column" className={pendingRequestsStyle.listWrapper}>
                <Grid item xs={12} className={pendingRequestsStyle.tableWrapper}>
                    <DataGrid
                        className={adminDashboardStyles.adminDashboardTable}
                        rows={rows}
                        columns={columns}
                        getRowId={(r: PendingAgentRow) => r.user_id}
                        density="compact"
                        hideFooterSelectedRowCount
                        rowHeight={60}
                        pageSize={10}
                        rowsPerPageOptions={[5]}
                        disableSelectionOnClick
                        autoHeight
                        components={{ Pagination: CustomPagination, Toolbar: CustomToolbar, NoRowsOverlay }}
                        sx={adminDashboardDataGridSx}
                    />
                </Grid>
            </Grid>
        </>
    );
};

export default PendingRequestsList;